import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Cookie, X } from 'lucide-react';
import { useColors } from '../hooks/useColors';

const STORAGE_KEY = 'pd_cookie_consent'; // "accepted" | "declined"

export default function CookieConsent() {
  const C = useColors();
  const [visible, setVisible] = useState(false);
  const [newsletterOpen, setNewsletterOpen] = useState(
    typeof window !== 'undefined' && window.__pdNewsletterOpen === true
  );

  useEffect(() => {
    try {
      const val = localStorage.getItem(STORAGE_KEY);
      if (!val) {
        // Slight delay so banner reveals after initial paint
        const t = setTimeout(() => setVisible(true), 600);
        return () => clearTimeout(t);
      }
    } catch {
      setVisible(true);
    }
  }, []);

  // Hide while newsletter modal is open; reveal once user decides on it
  useEffect(() => {
    const onOpen = () => setNewsletterOpen(true);
    const onClose = () => setNewsletterOpen(false);
    window.addEventListener('pd-newsletter-open', onOpen);
    window.addEventListener('pd-newsletter-close', onClose);
    return () => {
      window.removeEventListener('pd-newsletter-open', onOpen);
      window.removeEventListener('pd-newsletter-close', onClose);
    };
  }, []);

  const decide = (choice) => {
    try { localStorage.setItem(STORAGE_KEY, choice); } catch {}
    setVisible(false);
  };

  if (!visible || newsletterOpen) return null;

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-[60] px-4 pb-4 sm:px-6 sm:pb-6 pointer-events-none animate-[slide-up_0.4s_ease-out]"
      data-testid="cookie-consent-banner"
    >
      <div
        className="pointer-events-auto max-w-4xl mx-auto rounded-2xl border shadow-2xl backdrop-blur-2xl backdrop-saturate-150 flex flex-col sm:flex-row sm:items-center gap-4 p-4 sm:p-5"
        style={{
          background: C.theme === 'dark' ? 'rgba(10,10,14,0.85)' : 'rgba(255,255,255,0.92)',
          borderColor: C.border,
          WebkitBackdropFilter: 'blur(24px) saturate(150%)',
          boxShadow: `0 20px 60px ${C.theme === 'dark' ? 'rgba(0,0,0,0.6)' : 'rgba(15,23,42,0.18)'}`,
        }}
      >
        <div className="flex items-start sm:items-center gap-3 flex-1 min-w-0">
          <div
            className="w-10 h-10 rounded-xl grid place-items-center shrink-0"
            style={{ background: `${C.purple}14` }}
          >
            <Cookie className="w-5 h-5" style={{ color: C.cyan }} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold mb-0.5" style={{ color: C.text }}>
              We use cookies to improve your experience
            </p>
            <p className="text-[12px] leading-relaxed" style={{ color: C.text2 }}>
              We use cookies for analytics, personalization, and to remember your preferences. By continuing you
              agree to our{' '}
              <Link
                to="/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="underline underline-offset-2 hover:opacity-80"
                style={{ color: C.cyan }}
                data-testid="cookie-privacy-link"
              >
                Privacy Policy
              </Link>
              .
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0 self-stretch sm:self-auto">
          <button
            onClick={() => decide('declined')}
            className="flex-1 sm:flex-none px-4 py-2.5 rounded-xl text-sm font-semibold border transition-all "
            style={{ color: C.text2, borderColor: C.border, background: 'transparent' }}
            data-testid="cookie-decline-btn"
          >
            Decline
          </button>
          <button
            onClick={() => decide('accepted')}
            className="flex-1 sm:flex-none px-5 py-2.5 rounded-xl text-sm font-semibold text-white transition-all "
            style={{
              background: C.purple,
              boxShadow: `0 6px 24px ${C.purple}33`,
            }}
            data-testid="cookie-accept-btn"
          >
            Accept
          </button>
          <button
            onClick={() => decide('declined')}
            className="hidden sm:grid w-9 h-9 place-items-center rounded-xl border transition-all hover:bg-white/5"
            style={{ borderColor: C.border, color: C.muted }}
            aria-label="Dismiss"
            data-testid="cookie-close-btn"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
      <style>{`
        @keyframes slide-up {
          0%   { transform: translateY(20px); opacity: 0; }
          100% { transform: translateY(0);    opacity: 1; }
        }
      `}</style>
    </div>
  );
}
